import React, { useEffect, useState } from "react";
import OwCheckbox from "../../../../shared/OwCheckbox/OwCheckbox";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import Typeahead from "../../../../shared/Typeahead/Typeahead";
import ICatalog_ItemUI from "../../../../../models/ICatalog_ItemUI";
import IQBOGetItemByNameDTO from "../../../../../models/DTO/IQBOGetItemByNameDTO";
import IQBOGetItemByEntityIdDTO from "../../../../../models/DTO/IQBOGetItemByEntityIdDTO";
import IQBOOnGetItemByEntityIdDTO from "../../../../../models/DTO/IQBOOnGetItemByEntityIdDTO";
import EndpointsEnum from "../../../../../models/EndpointsEnum";
import IQBOItemDTO from "../../../../../models/QuickBooks Online/IQBOItemDTO";
import IUiSocketSetup from "../../../../../models/IUiSocketSetup";
import * as _ from "lodash-es";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import { OwIconTooltip } from "@components/shared/OwIconTooltip/OwIconTooltip";
import { useAppContextNonNull } from "@contexts/AppContext";
import OwButton from "@components/shared/OwButton/OwButton";

type Props = {
    createInQbo?: boolean;
    catalogItem?: ICatalog_ItemUI;
    updateStateFn: (state: any) => void;
};

type SearchFilterSelections = {
    name: boolean;
    sku: boolean;
    id: boolean;
};

export const CatalogItemSidebarQboSection = (props: Props) => {
    const { socket, userModel, supplierModel, uiContext } = useAppContextNonNull();

    const getSearchFilterSelections = (): SearchFilterSelections => {
        const selections = {
            name: true,
            sku: false,
            id: false,
        };

        return selections;
    };

    const [state, setState] = React.useState<{ create_in_qbo: boolean; has_inventory: boolean; manual_qbo_item?: IQBOItemDTO }>({
        create_in_qbo: props.createInQbo || false,
        has_inventory: false,
    });

    useEffect(() => {
        setState((prev) => ({ ...prev, create_in_qbo: props.createInQbo || false }));
    }, [props.createInQbo]);
    const [foundQboItems, setFoundQboItems] = React.useState<IQBOItemDTO[]>([]);
    const [searchFilterSelections, setSearchFilterSelections] = useState(getSearchFilterSelections());
    const [skipUpdating, setSkipUpdating] = useState(props.catalogItem?.qbo_skip_updating || false);
    const [skipSending, setSkipSending] = useState(props.catalogItem?.qbo_skip_sending || false);
    const newCatalogItem = !props.catalogItem?.guid;

    const onQboGetItemByName = (data: IQBOItemDTO[]) => {
        setFoundQboItems(data);
    };

    const onQboGetItemByEntityId = (data: IQBOOnGetItemByEntityIdDTO) => {
        console.log("QBO Item fetched by Entity ID:", data.item);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.QBOOnGetItemByName, fn: onQboGetItemByName },
        { endpoint: EndpointsEnum.QBOOnGetItemByEntityId, fn: onQboGetItemByEntityId },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const handleChange = (e: any) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const name = e.target.name;

        const stateUpdate: any = {
            [name]: value,
        };

        if (name === "create_in_qbo" && newCatalogItem) {
            stateUpdate.has_inventory = false;
        }

        setState(stateUpdate);
        props.updateStateFn(stateUpdate);
    };

    const handleBoolChange = (name: string, value: boolean) => {
        const stateUpdate: any = {
            [name]: value,
        };

        setState({ ...state, ...stateUpdate });
        props.updateStateFn(stateUpdate);

        if (name === "qbo_skip_updating") {
            setSkipUpdating(value);
        } else if (name === "qbo_skip_sending") {
            setSkipSending(value);
        }
    };

    const getType = (selections: SearchFilterSelections): "name" | "sku" | "id" => {
        let type: "name" | "sku" | "id" = "sku";

        if (selections.name) {
            type = "name";
        } else if (selections.id) {
            type = "id";
        }
        return type;
    };

    const lookupQboItemByName = (name: string) => {
        const type = getType(searchFilterSelections);

        const payload: IQBOGetItemByNameDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            name,
            type,
        };

        socket.emit(EndpointsEnum.QBOGetItemByName, payload);
    };

    const renderQboItem = (item: IQBOItemDTO) => {
        return (
            <div>
                <div>
                    Name: {item.Name} - Type: {item.Type} - Id: {item.Id}
                </div>
                <div>Sku: {item.Sku}</div>
            </div>
        );
    };

    const selectManualQboItem = (item: IQBOItemDTO) => {
        setState({
            ...state,
            manual_qbo_item: item,
        });

        props.updateStateFn({ manual_qbo_item: item });
    };

    const toggleSearchFilter = (item: string) => {
        // we only allow one filter at a time

        const _searchFilterSelections = {
            name: false,
            sku: false,
            id: false,
        };

        // @ts-expect-error
        _searchFilterSelections[item] = !_searchFilterSelections[item];

        setSearchFilterSelections(_searchFilterSelections);
    };

    const fetchQboItemByEntityId = () => {
        let entityId: string | undefined;

        if (state.manual_qbo_item?.Id) {
            entityId = state.manual_qbo_item.Id;
            console.log(`Fetching QBO item using manual item Entity ID: ${entityId}`);
        } else if (props.catalogItem?.qbo_item_id) {
            entityId = String(props.catalogItem.qbo_item_id);
            console.log(`Fetching QBO item using catalog item Entity ID: ${entityId}`);
        } else {
            console.log("No Entity ID available to fetch QBO item");
            return;
        }

        const payload: IQBOGetItemByEntityIdDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            entityId: entityId,
            catalogItemGuid: props.catalogItem?.guid,
        };

        socket.emit(EndpointsEnum.QBOGetItemByEntityId, payload);
    };

    return (
        <div className={"CatalogItemSidebarQboSection"}>
            <div className="bd-callout">
                <OwLabel title={"quickbooks_online"} />
            </div>
            {newCatalogItem ? (
                <div>
                    <div>
                        <OwToggle
                            label={"create_in_quickbooks_online"}
                            name={"create_in_qbo"}
                            active={state.create_in_qbo}
                            fn={handleChange}
                            labelOnRight={true}
                        />
                    </div>
                </div>
            ) : null}

            {!newCatalogItem ? (
                <div>
                    <div className="mt-2 d-flex align-items-center">
                        <OwToggle
                            labelOnRight={true}
                            label={"skip_updating"}
                            name={"qbo_skip_updating"}
                            active={skipUpdating}
                            fn={() => handleBoolChange("qbo_skip_updating", !skipUpdating)}
                        />
                        <OwIconTooltip
                            translateContent={true}
                            tooltipContent={"help_text_qbo_skip_updating"}
                            iconClassName="ms-2 text-muted fas fa-info-circle"
                        />
                    </div>
                    <div className="mt-2 d-flex align-items-center">
                        <OwToggle
                            labelOnRight={true}
                            label={"skip_sending"}
                            name={"qbo_skip_sending"}
                            active={skipSending}
                            fn={() => handleBoolChange("qbo_skip_sending", !skipSending)}
                        />
                        <OwIconTooltip
                            translateContent={true}
                            tooltipContent={"help_text_qbo_skip_sending"}
                            iconClassName="ms-2 text-muted fas fa-info-circle"
                        />
                    </div>
                    <div className="mt-2">
                        <OwToggle
                            labelOnRight={true}
                            label={"as_description_line"}
                            name={"qbo_as_description_line"}
                            active={props.catalogItem?.qbo_as_description_line || false}
                            fn={() => handleBoolChange("qbo_as_description_line", !props.catalogItem?.qbo_as_description_line)}
                        />
                    </div>
                    <div className="mt-2">
                        <div>
                            <OwLabel title={"qbo_item_id"} postText={":"} className={"me-2"} />
                            {props.catalogItem?.qbo_item_id}
                        </div>
                    </div>
                    <div>
                        <div>
                            <OwLabel title={"qbo_name"} postText={":"} className={"me-2"} />
                            {props.catalogItem?.qbo_name}
                        </div>
                    </div>
                    <div className="mt-2">
                        <div>
                            <OwLabel title={"manual_item_lookup"} />
                        </div>
                        <div>
                            <Typeahead
                                sideFilter={true}
                                sideFilterItems={[
                                    {
                                        name: "name",
                                        children: (
                                            <OwCheckbox
                                                label={"name"}
                                                name={"name"}
                                                checked={searchFilterSelections.name}
                                                fn={() => toggleSearchFilter("name")}
                                            />
                                        ),
                                    },
                                    {
                                        name: "sku",
                                        children: (
                                            <OwCheckbox
                                                label={"sku"}
                                                name={"sku"}
                                                checked={searchFilterSelections.sku}
                                                fn={() => toggleSearchFilter("sku")}
                                            />
                                        ),
                                    },
                                    {
                                        name: "id",
                                        children: (
                                            <OwCheckbox
                                                label={"id"}
                                                checked={searchFilterSelections.id}
                                                name={"id"}
                                                fn={() => toggleSearchFilter("id")}
                                            />
                                        ),
                                    },
                                ]}
                                isSelectableFn={() => true}
                                typeaheadApiFn={lookupQboItemByName}
                                itemsRenderFn={renderQboItem}
                                titleProp={"Name"}
                                selectedItemFn={selectManualQboItem}
                                selectedItemTitleProp={"Name"}
                                typeaheadItems={foundQboItems}
                                subTitleProp={"Sku"}
                                scrollableList={true}
                            />
                        </div>
                    </div>
                    {uiContext.showHiddenFeatures ? (
                        <div className="mt-3">
                            <OwButton title={"Fetch QBO Item by Entity ID"} fn={fetchQboItemByEntityId} leftIconClass="fas fa-search" />
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};
